import React, { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useClients } from '@/hooks/useClients'
import { useCampaigns } from '@/hooks/useCampaigns'
import { useContacts } from '@/hooks/useContacts'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, Globe, Link, CheckSquare, Users, ChevronsUpDown, Plus, Trash2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import type { AngleOfferEntry } from '@/lib/campaign-angles-offers'

// Normalise a campaign name: trim and collapse any internal whitespace runs
// to a single space. Prevents "Cruva  - Bulk" from being stored as a new
// variant of "Cruva - Bulk". Used on submit for both Create and Edit.
function normaliseCampaignName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ')
}

// Phase C: Campaign Structure draft types. Mirrors the createCampaign hook's
// CampaignStructure but kept local so the form can build it incrementally
// before passing it to the hook.
interface SegmentDraft {
  name: string
  description: string
  angles: AngleOfferEntry[]
  offers: AngleOfferEntry[]
  /** Phase H: per-segment subject_lines (hybrid with campaign-level). */
  subject_lines: AngleOfferEntry[]
}

/** Code prefix per dimension. */
type DimPrefix = 'A' | 'O' | 'R' | 'S'

/** Generate the next code for a dimension list. A1/O1/R1/S1 etc. */
function nextCode(prefix: DimPrefix, existing: AngleOfferEntry[]): string {
  let n = existing.length + 1
  while (existing.some((e) => e.code === `${prefix}${n}`)) n += 1
  return `${prefix}${n}`
}

/** Resize a list of entries to the target length, preserving existing data. */
function resizeEntries(
  current: AngleOfferEntry[],
  target: number,
  prefix: DimPrefix
): AngleOfferEntry[] {
  if (target <= 0) return []
  if (target === current.length) return current
  if (target < current.length) return current.slice(0, target)
  // target > current: append blanks with auto-codes
  const next = [...current]
  while (next.length < target) {
    next.push({ code: nextCode(prefix, next), name: '', description: '' })
  }
  return next
}

/** Resize a list of segments to target length, preserving existing drafts. */
function resizeSegments(current: SegmentDraft[], target: number): SegmentDraft[] {
  if (target <= 0) return []
  if (target === current.length) return current
  if (target < current.length) return current.slice(0, target)
  const next = [...current]
  while (next.length < target) {
    next.push({ name: '', description: '', angles: [], offers: [], subject_lines: [] })
  }
  return next
}

interface CreateCampaignDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function CreateCampaignDialog({ open, onOpenChange, onSuccess }: CreateCampaignDialogProps) {
  // Removed excessive debug logging to prevent console spam
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    description: '',
    campaign_type: '',
    trigger_type: '',
    // Pre-checked with 'email' because the wizard auto-creates variants
    // with defaultChannel='email'. Previously this started empty, leaving
    // campaigns with channels=[] while their variants had channel='email'.
    // User can uncheck Email or add LinkedIn/Cold Call as needed.
    channels: ['email'],
    version: '1.0',
    target_regions: [],
    clay_table_link: '',
    clickup_task_link: '',
    copywriting_doc_link: '',
    previous_campaign_id: '',
  })
  const [tamFile, setTamFile] = useState<File | null>(null)
  const [tamSource, setTamSource] = useState<'file' | 'crm'>('file')
  const [selectedContacts, setSelectedContacts] = useState<string[]>([])

  // Phase C: Campaign Structure wizard state. The user enters a number of
  // segments → segment draft cards expand. For each segment they enter
  // angle/offer counts → name boxes expand. If 0 segments, angles + offers
  // live at the campaign level instead.
  const [segmentCount, setSegmentCount] = useState(0)
  const [segments, setSegments] = useState<SegmentDraft[]>([])
  const [campaignAngles, setCampaignAngles] = useState<AngleOfferEntry[]>([])
  const [campaignOffers, setCampaignOffers] = useState<AngleOfferEntry[]>([])
  // Phase H: region is campaign-only (Q4); subject_lines are hybrid (Q6)
  // so users can optionally set campaign-level defaults AND override
  // per-segment.
  const [campaignRegions, setCampaignRegions] = useState<AngleOfferEntry[]>([])
  const [campaignSubjectLines, setCampaignSubjectLines] = useState<AngleOfferEntry[]>([])
  // Phase F+: optional per-variant overrides keyed by the cell triple
  // `${segmentIdx}|${angleCode}|${offerCode}` (segmentIdx = -1 for
  // 0-segment campaigns). Only cells the user actually types into get
  // stored; auto-naming + empty Smartlead URL are used otherwise.
  const [variantOverrides, setVariantOverrides] = useState<
    Record<string, { name?: string; smartlead_campaign_url?: string; heyreach_campaign_url?: string }>
  >({})
  const [showVariantDetails, setShowVariantDetails] = useState(false)

  // Helper: resize segments when the count changes. Wrapped to also keep
  // the count number in sync if the user manually adds/removes segments.
  const handleSegmentCountChange = (raw: string) => {
    const n = Math.max(0, parseInt(raw, 10) || 0)
    setSegmentCount(n)
    setSegments((prev) => resizeSegments(prev, n))
  }

  const updateSegmentField = (idx: number, patch: Partial<SegmentDraft>) => {
    setSegments((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)))
  }

  const updateSegmentAngles = (idx: number, target: number) => {
    setSegments((prev) =>
      prev.map((s, i) =>
        i === idx ? { ...s, angles: resizeEntries(s.angles, target, 'A') } : s
      )
    )
  }
  const updateSegmentOffers = (idx: number, target: number) => {
    setSegments((prev) =>
      prev.map((s, i) =>
        i === idx ? { ...s, offers: resizeEntries(s.offers, target, 'O') } : s
      )
    )
  }
  const updateSegmentSubjectLines = (idx: number, target: number) => {
    setSegments((prev) =>
      prev.map((s, i) =>
        i === idx ? { ...s, subject_lines: resizeEntries(s.subject_lines, target, 'S') } : s
      )
    )
  }
  const updateSegmentEntryName = (
    segIdx: number,
    kind: 'angles' | 'offers' | 'subject_lines',
    entryIdx: number,
    name: string
  ) => {
    setSegments((prev) =>
      prev.map((s, i) =>
        i === segIdx
          ? {
              ...s,
              [kind]: s[kind].map((e, j) => (j === entryIdx ? { ...e, name } : e)),
            }
          : s
      )
    )
  }

  // Variant cells for the optional per-variant inputs section. Enumerates
  // the same 5-D cells the hook will auto-create on save:
  //   segment × angle × offer × region × subject_line
  // `segmentIdx = -1` for 0-segment campaigns; any of the dimension codes
  // are empty strings when that dimension is missing for the cell. Keep
  // keys in sync with useCampaigns.ts buildVariantRowsForCell().
  const variantCells = useMemo(() => {
    type Cell = {
      key: string
      segmentIdx: number
      segmentName?: string
      angleCode: string
      angleName?: string
      offerCode: string
      offerName?: string
      regionCode: string
      regionName?: string
      subjectCode: string
      subjectName?: string
    }
    const cells: Cell[] = []

    const addCellsFor = (
      segmentIdx: number,
      segmentName: string | undefined,
      angles: AngleOfferEntry[],
      offers: AngleOfferEntry[],
      subjectLines: AngleOfferEntry[],
    ) => {
      const allEmpty =
        angles.length === 0 &&
        offers.length === 0 &&
        campaignRegions.length === 0 &&
        subjectLines.length === 0
      if (allEmpty) return
      const angleList = angles.length > 0 ? angles : [null]
      const offerList = offers.length > 0 ? offers : [null]
      const regionList = campaignRegions.length > 0 ? campaignRegions : [null]
      const subjectList = subjectLines.length > 0 ? subjectLines : [null]
      for (const a of angleList) {
        for (const o of offerList) {
          for (const r of regionList) {
            for (const s of subjectList) {
              const ac = a?.code ?? ''
              const oc = o?.code ?? ''
              const rc = r?.code ?? ''
              const sc = s?.code ?? ''
              cells.push({
                key: `${segmentIdx}|${ac}|${oc}|${rc}|${sc}`,
                segmentIdx,
                segmentName,
                angleCode: ac,
                angleName: a?.name,
                offerCode: oc,
                offerName: o?.name,
                regionCode: rc,
                regionName: r?.name,
                subjectCode: sc,
                subjectName: s?.name,
              })
            }
          }
        }
      }
    }

    if (segmentCount === 0) {
      addCellsFor(-1, undefined, campaignAngles, campaignOffers, campaignSubjectLines)
    } else {
      // Per-segment subject_lines hybrid: segment.subject_lines wins if
      // non-empty, else campaignSubjectLines fallback (matches hook logic).
      segments.forEach((seg, i) => {
        const effSubj = seg.subject_lines.length > 0 ? seg.subject_lines : campaignSubjectLines
        addCellsFor(i, seg.name, seg.angles, seg.offers, effSubj)
      })
    }
    return cells
  }, [segmentCount, segments, campaignAngles, campaignOffers, campaignRegions, campaignSubjectLines])

  const variantCellCount = variantCells.length

  const updateVariantOverride = (
    key: string,
    patch: Partial<{ name: string; smartlead_campaign_url: string; heyreach_campaign_url: string }>,
  ) => {
    setVariantOverrides((prev) => {
      const next = { ...prev, [key]: { ...prev[key], ...patch } }
      // Drop keys where all three fields are empty so we don't send noise.
      const e = next[key]
      if (
        !e.name?.trim() &&
        !e.smartlead_campaign_url?.trim() &&
        !e.heyreach_campaign_url?.trim()
      ) {
        const { [key]: _drop, ...rest } = next
        return rest
      }
      return next
    })
  }

  // LinkedIn variant cells: unique (segment × angle × offer) tuples because
  // LI variants skip region + subject_line per the Storystream/NRF convention.
  // Keys use the SAME shape the hook's buildVariantRowsForCell generates for
  // channel='linkedin': `${segmentIdx}|${angle}|${offer}||` (empty region/
  // subject so the override lookup hits the right variant).
  const linkedInVariantCells = useMemo(() => {
    if (!formData.channels.includes('linkedin')) return []
    type LiCell = {
      key: string
      segmentIdx: number
      segmentName?: string
      angleCode: string
      angleName?: string
      offerCode: string
      offerName?: string
    }
    const cells: LiCell[] = []
    const addLi = (
      segmentIdx: number,
      segmentName: string | undefined,
      angles: AngleOfferEntry[],
      offers: AngleOfferEntry[],
    ) => {
      const angleList = angles.length > 0 ? angles : [null]
      const offerList = offers.length > 0 ? offers : [null]
      // Skip only when BOTH are empty — otherwise the LI loop still produces
      // one variant per (angle/offer) cell (matching hook behaviour).
      if (angles.length === 0 && offers.length === 0) return
      for (const a of angleList) {
        for (const o of offerList) {
          const ac = a?.code ?? ''
          const oc = o?.code ?? ''
          cells.push({
            key: `${segmentIdx}|${ac}|${oc}||`,
            segmentIdx,
            segmentName,
            angleCode: ac,
            angleName: a?.name,
            offerCode: oc,
            offerName: o?.name,
          })
        }
      }
    }
    if (segmentCount === 0) {
      addLi(-1, undefined, campaignAngles, campaignOffers)
    } else {
      segments.forEach((seg, i) => addLi(i, seg.name, seg.angles, seg.offers))
    }
    return cells
  }, [formData.channels, segmentCount, segments, campaignAngles, campaignOffers])
  
  const { data: clients = [] } = useClients()
  const { campaigns, createCampaign, loading } = useCampaigns()
  const { contacts } = useContacts(undefined, formData.client_id ? parseInt(formData.client_id) : undefined)
  const { toast } = useToast()

  // Filter campaigns by selected client
  const availableCampaigns = campaigns.filter(campaign =>
    campaign.client_id === formData.client_id
  )

  // Distinct existing campaign names for the selected client. Feeds the
  // Campaign Name combobox so users pick "Cruva - Bulk" from a list instead
  // of typing (and drifting into "cruva bulk", "Cruva-Bulk", etc.).
  const existingNamesForClient = useMemo(() => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const c of availableCampaigns) {
      const n = (c.name || '').trim()
      if (!n) continue
      const key = n.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push(n)
    }
    return out.sort((a, b) => a.localeCompare(b))
  }, [availableCampaigns])

  const [nameComboOpen, setNameComboOpen] = useState(false)

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setTamFile(acceptedFiles[0])
      }
    },
    onDropRejected: () => {
      toast({
        title: "Invalid file",
        description: "Please upload a CSV or Excel file",
        variant: "destructive",
      })
    }
  })

  const [isCreating, setIsCreating] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (isCreating) return // Prevent double submission
    
    console.log('Create campaign submitted with data:', formData)
    
    // Normalise before validation so "  Cruva  -  Bulk  " is treated as
    // "Cruva - Bulk" for both the required-field check and the actual save.
    const normalisedName = normaliseCampaignName(formData.name)

    if (!normalisedName || !formData.client_id) {
      console.log('Validation failed:', { name: formData.name, client_id: formData.client_id })
      toast({
        title: "Validation Error",
        description: "Client and Campaign Name are both required.",
        variant: "destructive",
      })
      return
    }

    setIsCreating(true)

    try {
      console.log('Calling createCampaign with:', {
        ...formData,
        name: normalisedName,
        tamFile: tamFile || undefined,
      })

      // Show loading toast
      toast({
        title: "Creating Campaign",
        description: "Please wait while we create your campaign...",
      })

      await createCampaign({
        ...formData,
        name: normalisedName,
        tamFile: tamFile || undefined,
        // Phase C + Phase H: pass the 5-D campaign structure so the hook
        // auto-creates segments + variants for each (segment × angle ×
        // offer × region × subject_line) cell.
        structure: {
          segments: segments
            .filter((s) => s.name.trim())
            .map((s) => ({
              name: s.name.trim(),
              description: s.description.trim() || undefined,
              // Drop entries with no name (user added a slot but didn't fill it).
              angles: s.angles.filter((a) => a.name.trim()).map((a) => ({
                code: a.code,
                name: a.name.trim(),
                description: a.description?.trim() || undefined,
              })),
              offers: s.offers.filter((o) => o.name.trim()).map((o) => ({
                code: o.code,
                name: o.name.trim(),
                description: o.description?.trim() || undefined,
              })),
              subject_lines: s.subject_lines.filter((x) => x.name.trim()).map((x) => ({
                code: x.code,
                name: x.name.trim(),
                description: x.description?.trim() || undefined,
              })),
            })),
          campaignAngles: campaignAngles.filter((a) => a.name.trim()).map((a) => ({
            code: a.code,
            name: a.name.trim(),
            description: a.description?.trim() || undefined,
          })),
          campaignOffers: campaignOffers.filter((o) => o.name.trim()).map((o) => ({
            code: o.code,
            name: o.name.trim(),
            description: o.description?.trim() || undefined,
          })),
          campaignRegions: campaignRegions.filter((r) => r.name.trim()).map((r) => ({
            code: r.code,
            name: r.name.trim(),
            description: r.description?.trim() || undefined,
          })),
          campaignSubjectLines: campaignSubjectLines.filter((x) => x.name.trim()).map((x) => ({
            code: x.code,
            name: x.name.trim(),
            description: x.description?.trim() || undefined,
          })),
          // Auto-create one variant per dimension cell for EVERY channel
          // the user ticked. LinkedIn variants skip the region_code and
          // subject_line_code dimensions per the Storystream / NRF
          // convention — the hook handles that stripping.
          channels: formData.channels.length > 0 ? formData.channels : ['email'],
          variantOverrides,
        },
      })

      console.log('Campaign created successfully')

      // Reset form
      setFormData({
        name: '',
        client_id: '',
        description: '',
        campaign_type: '',
        trigger_type: '',
        channels: ['email'], // mirror the default from initial state
        version: '1.0',
        target_regions: [],
        clay_table_link: '',
        clickup_task_link: '',
        copywriting_doc_link: '',
        previous_campaign_id: '',
      })
      setTamFile(null)
      setTamSource('file')
      setSelectedContacts([])
      // Reset Phase C+H structure state too
      setSegmentCount(0)
      setSegments([])
      setCampaignAngles([])
      setCampaignOffers([])
      setCampaignRegions([])
      setCampaignSubjectLines([])
      setVariantOverrides({})
      setShowVariantDetails(false)
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Error creating campaign:', error)
      // Error is handled in the hook
    } finally {
      setIsCreating(false)
    }
  }

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleRegionChange = (region: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      target_regions: checked 
        ? [...prev.target_regions, region]
        : prev.target_regions.filter(r => r !== region)
    }))
  }

  const handleChannelChange = (channel: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      channels: checked 
        ? [...prev.channels, channel]
        : prev.channels.filter(c => c !== channel)
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Campaign</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campaign Structure moved to the TOP of the form (per Sabeeh's
              feedback on 2026-04-22). Segments → angles → offers is the
              primary shape of a campaign; everything else (client, name,
              links, TAM) is metadata. */}
          <Card>
            <CardHeader>
              <CardTitle>Campaign Structure</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="segment_count">How many segments?</Label>
                <Input
                  id="segment_count"
                  type="number"
                  min="0"
                  max="20"
                  value={segmentCount}
                  onChange={(e) => handleSegmentCountChange(e.target.value)}
                  className="w-32"
                />
                <p className="text-xs text-muted-foreground">
                  Audience cuts (e.g. "Not On TTS", "AOV", "Community Building").
                  Set to 0 for campaigns with no audience segmentation
                  (Duel-style: angles + offers only).
                </p>
              </div>

              {/* If 0 segments → campaign-level Angles + Offers + Regions
                  + Subject Lines (all four dimensions live at campaign level
                  for a Duel-style campaign). */}
              {segmentCount === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md border p-3 bg-muted/20">
                  <div className="space-y-2">
                    <Label>How many angles? (campaign-wide)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="20"
                      value={campaignAngles.length}
                      onChange={(e) => {
                        const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                        setCampaignAngles((prev) => resizeEntries(prev, n, 'A'))
                      }}
                      className="w-32"
                    />
                    {campaignAngles.map((a, i) => (
                      <div key={a.code} className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground w-10">{a.code}</span>
                        <Input
                          value={a.name}
                          onChange={(e) =>
                            setCampaignAngles((prev) =>
                              prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                            )
                          }
                          placeholder={`Angle ${i + 1} name`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Label>How many offers? (campaign-wide)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="20"
                      value={campaignOffers.length}
                      onChange={(e) => {
                        const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                        setCampaignOffers((prev) => resizeEntries(prev, n, 'O'))
                      }}
                      className="w-32"
                    />
                    {campaignOffers.map((o, i) => (
                      <div key={o.code} className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground w-10">{o.code}</span>
                        <Input
                          value={o.name}
                          onChange={(e) =>
                            setCampaignOffers((prev) =>
                              prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                            )
                          }
                          placeholder={`Offer ${i + 1} name`}
                        />
                      </div>
                    ))}
                  </div>
                  {/* Phase H: regions (campaign-only) */}
                  <div className="space-y-2">
                    <Label>How many regions?</Label>
                    <Input
                      type="number"
                      min="0"
                      max="20"
                      value={campaignRegions.length}
                      onChange={(e) => {
                        const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                        setCampaignRegions((prev) => resizeEntries(prev, n, 'R'))
                      }}
                      className="w-32"
                    />
                    {campaignRegions.map((r, i) => (
                      <div key={r.code} className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground w-10">{r.code}</span>
                        <Input
                          value={r.name}
                          onChange={(e) =>
                            setCampaignRegions((prev) =>
                              prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                            )
                          }
                          placeholder={`Region ${i + 1} (e.g. EU, USA)`}
                        />
                      </div>
                    ))}
                  </div>
                  {/* Phase H: campaign-level subject lines (hybrid) */}
                  <div className="space-y-2">
                    <Label>How many subject lines? (campaign-wide)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="20"
                      value={campaignSubjectLines.length}
                      onChange={(e) => {
                        const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                        setCampaignSubjectLines((prev) => resizeEntries(prev, n, 'S'))
                      }}
                      className="w-32"
                    />
                    {campaignSubjectLines.map((s, i) => (
                      <div key={s.code} className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground w-10">{s.code}</span>
                        <Input
                          value={s.name}
                          onChange={(e) =>
                            setCampaignSubjectLines((prev) =>
                              prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                            )
                          }
                          placeholder={`Subject line ${i + 1} (e.g. Old, New)`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* If N>0 segments → campaign-level Regions + (fallback)
                  Subject Lines still render here. Regions are campaign-only
                  (Q4); Subject Lines are hybrid (Q6) and can be overridden
                  per-segment below. */}
              {segmentCount > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md border p-3 bg-muted/20">
                  <div className="space-y-2">
                    <Label>How many regions? (campaign-wide)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="20"
                      value={campaignRegions.length}
                      onChange={(e) => {
                        const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                        setCampaignRegions((prev) => resizeEntries(prev, n, 'R'))
                      }}
                      className="w-32"
                    />
                    {campaignRegions.map((r, i) => (
                      <div key={r.code} className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground w-10">{r.code}</span>
                        <Input
                          value={r.name}
                          onChange={(e) =>
                            setCampaignRegions((prev) =>
                              prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                            )
                          }
                          placeholder={`Region ${i + 1} (e.g. EU, USA)`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Label>Campaign-wide subject lines (fallback)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="20"
                      value={campaignSubjectLines.length}
                      onChange={(e) => {
                        const n = Math.max(0, parseInt(e.target.value, 10) || 0)
                        setCampaignSubjectLines((prev) => resizeEntries(prev, n, 'S'))
                      }}
                      className="w-32"
                    />
                    {campaignSubjectLines.map((s, i) => (
                      <div key={s.code} className="flex items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground w-10">{s.code}</span>
                        <Input
                          value={s.name}
                          onChange={(e) =>
                            setCampaignSubjectLines((prev) =>
                              prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x))
                            )
                          }
                          placeholder={`Subject line ${i + 1}`}
                        />
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">
                      Used for segments that don't define their own subject
                      lines below. Leave empty if every segment sets its own.
                    </p>
                  </div>
                </div>
              )}

              {/* If N>0 segments → per-segment cards */}
              {segmentCount > 0 && segments.map((seg, segIdx) => (
                <div key={segIdx} className="rounded-md border p-3 space-y-3 bg-muted/20">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs uppercase font-mono tracking-wider text-muted-foreground">
                        Segment {segIdx + 1}
                      </Label>
                      <Input
                        value={seg.name}
                        onChange={(e) => updateSegmentField(segIdx, { name: e.target.value })}
                        placeholder="e.g. Not On TTS"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs uppercase font-mono tracking-wider text-muted-foreground">
                        Description (optional)
                      </Label>
                      <Input
                        value={seg.description}
                        onChange={(e) => updateSegmentField(segIdx, { description: e.target.value })}
                        placeholder="What audience this targets"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t">
                    <div className="space-y-2">
                      <Label>Angles for this segment</Label>
                      <Input
                        type="number"
                        min="0"
                        max="20"
                        value={seg.angles.length}
                        onChange={(e) =>
                          updateSegmentAngles(
                            segIdx,
                            Math.max(0, parseInt(e.target.value, 10) || 0)
                          )
                        }
                        className="w-32"
                      />
                      {seg.angles.map((a, i) => (
                        <div key={a.code} className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground w-10">{a.code}</span>
                          <Input
                            value={a.name}
                            onChange={(e) =>
                              updateSegmentEntryName(segIdx, 'angles', i, e.target.value)
                            }
                            placeholder={`Angle ${i + 1} name`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      <Label>Offers for this segment</Label>
                      <Input
                        type="number"
                        min="0"
                        max="20"
                        value={seg.offers.length}
                        onChange={(e) =>
                          updateSegmentOffers(
                            segIdx,
                            Math.max(0, parseInt(e.target.value, 10) || 0)
                          )
                        }
                        className="w-32"
                      />
                      {seg.offers.map((o, i) => (
                        <div key={o.code} className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground w-10">{o.code}</span>
                          <Input
                            value={o.name}
                            onChange={(e) =>
                              updateSegmentEntryName(segIdx, 'offers', i, e.target.value)
                            }
                            placeholder={`Offer ${i + 1} name`}
                          />
                        </div>
                      ))}
                    </div>
                    {/* Phase H: per-segment subject_lines (hybrid; leave
                        at 0 to use campaign-wide fallback above). */}
                    <div className="space-y-2">
                      <Label>Subject lines (optional)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="20"
                        value={seg.subject_lines.length}
                        onChange={(e) =>
                          updateSegmentSubjectLines(
                            segIdx,
                            Math.max(0, parseInt(e.target.value, 10) || 0)
                          )
                        }
                        className="w-32"
                      />
                      {seg.subject_lines.map((s, i) => (
                        <div key={s.code} className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground w-10">{s.code}</span>
                          <Input
                            value={s.name}
                            onChange={(e) =>
                              updateSegmentEntryName(segIdx, 'subject_lines', i, e.target.value)
                            }
                            placeholder={`Subject line ${i + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Optional per-variant inputs. Matches the fields available
                  on the manual "+ Create Variant" dialog so users can fill
                  Smartlead links upfront if they already have them, or
                  defer and edit later. */}
              {variantCellCount > 0 && (
                <div className="rounded-md border bg-muted/10 p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">
                        Per-variant details{' '}
                        <span className="text-xs text-muted-foreground font-normal">
                          (optional — fill Smartlead link now or later)
                        </span>
                      </Label>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowVariantDetails((v) => !v)}
                    >
                      {showVariantDetails ? 'Hide' : `Show ${variantCellCount} row${variantCellCount === 1 ? '' : 's'}`}
                    </Button>
                  </div>
                  {showVariantDetails && (
                    <div className="space-y-2">
                      {variantCells.map((cell) => {
                        const defaultName =
                          [cell.angleName, cell.offerName, cell.regionName, cell.subjectName]
                            .filter(Boolean)
                            .join(' · ') || 'Variant'
                        const labelParts: string[] = []
                        if (cell.segmentName) labelParts.push(cell.segmentName)
                        if (cell.angleCode) labelParts.push(cell.angleCode)
                        if (cell.offerCode) labelParts.push(cell.offerCode)
                        if (cell.regionCode) labelParts.push(cell.regionCode)
                        if (cell.subjectCode) labelParts.push(cell.subjectCode)
                        const label = labelParts.join(' · ') || defaultName
                        const ovr = variantOverrides[cell.key] || {}
                        return (
                          <div
                            key={cell.key}
                            className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-2 items-center border-b last:border-b-0 pb-2 last:pb-0"
                          >
                            <div className="text-xs font-mono text-muted-foreground truncate" title={label}>
                              {label}
                            </div>
                            <Input
                              value={ovr.name ?? ''}
                              onChange={(e) => updateVariantOverride(cell.key, { name: e.target.value })}
                              placeholder={defaultName}
                              className="h-8 text-sm"
                            />
                            <Input
                              type="url"
                              value={ovr.smartlead_campaign_url ?? ''}
                              onChange={(e) =>
                                updateVariantOverride(cell.key, { smartlead_campaign_url: e.target.value })
                              }
                              placeholder="https://app.smartlead.ai/app/email-campaign/{id}/analytics"
                              className="h-8 text-sm"
                            />
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* LinkedIn variants — only when LI channel is ticked. Cells are
                  unique (segment × angle × offer) tuples; LI drops region +
                  subject_line per the Storystream/NRF rebuild convention.
                  HeyReach URL pattern is parsed by the auto-extract trigger on
                  insert; users can also paste URLs later via the leaf-row
                  action menu. */}
              {formData.channels.includes('linkedin') && linkedInVariantCells.length > 0 && (
                <div className="rounded-md border p-3 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      LinkedIn variants{' '}
                      <span className="text-muted-foreground font-normal">
                        ({linkedInVariantCells.length} row{linkedInVariantCells.length === 1 ? '' : 's'} — segment × angle × offer, no region/SL)
                      </span>
                    </span>
                  </div>
                  <div className="space-y-2">
                    {linkedInVariantCells.map((cell) => {
                      const defaultName =
                        [cell.angleName, cell.offerName].filter(Boolean).join(' · ') || 'LinkedIn Variant'
                      const labelParts: string[] = []
                      if (cell.segmentName) labelParts.push(cell.segmentName)
                      if (cell.angleCode) labelParts.push(cell.angleCode)
                      if (cell.offerCode) labelParts.push(cell.offerCode)
                      labelParts.push('LinkedIn')
                      const label = labelParts.join(' · ')
                      const ovr = variantOverrides[cell.key] || {}
                      return (
                        <div
                          key={cell.key}
                          className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-2 items-center border-b last:border-b-0 pb-2 last:pb-0"
                        >
                          <div className="text-xs font-mono text-muted-foreground truncate" title={label}>
                            {label}
                          </div>
                          <Input
                            value={ovr.name ?? ''}
                            onChange={(e) => updateVariantOverride(cell.key, { name: e.target.value })}
                            placeholder={defaultName}
                            className="h-8 text-sm"
                          />
                          <Input
                            type="url"
                            value={ovr.heyreach_campaign_url ?? ''}
                            onChange={(e) =>
                              updateVariantOverride(cell.key, { heyreach_campaign_url: e.target.value })
                            }
                            placeholder="https://app.heyreach.io/app/campaign/view/{id}/performance"
                            className="h-8 text-sm"
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Variant preview */}
              <div className="rounded-md border bg-blue-50/40 p-3">
                <p className="text-sm">
                  On save, will auto-create{' '}
                  <span className="font-mono font-semibold">{variantCellCount}</span>{' '}
                  email variant row{variantCellCount === 1 ? '' : 's'}
                  {formData.channels.includes('linkedin') && linkedInVariantCells.length > 0 && (
                    <>
                      {' + '}
                      <span className="font-mono font-semibold">{linkedInVariantCells.length}</span>{' '}
                      LinkedIn variant row{linkedInVariantCells.length === 1 ? '' : 's'}
                    </>
                  )}
                  {variantCellCount > 0 && ' (Smartlead + HeyReach links optional — fill now above, or per-variant later via Edit)'}
                  {variantCellCount === 0 && ' (no angles or offers defined yet — campaign will be empty)'}
                  .
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Client first, then Campaign Name. Name is gated on client_id so
              the combobox has a client's existing campaigns to suggest,
              preventing "Cruva Bulk" / "Cruva-Bulk" / "Cruva - Bulk " drift. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="client">Client *</Label>
              <Select
                value={formData.client_id}
                onValueChange={(value) => {
                  handleInputChange('client_id', value)
                  // Clear name when client changes so stale names from another
                  // client can't be submitted for a different client.
                  handleInputChange('name', '')
                }}
              >
                <SelectTrigger aria-required="true">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Campaign Name *</Label>
              <Popover open={nameComboOpen} onOpenChange={setNameComboOpen}>
                <div className="flex gap-2">
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onFocus={() => {
                      if (formData.client_id && existingNamesForClient.length > 0) {
                        setNameComboOpen(true)
                      }
                    }}
                    placeholder={
                      formData.client_id
                        ? 'Pick an existing campaign name or type a new one'
                        : 'Select a client first'
                    }
                    disabled={!formData.client_id}
                    required
                    aria-required="true"
                    className="flex-1"
                  />
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={!formData.client_id || existingNamesForClient.length === 0}
                      aria-label="Show existing campaign names for this client"
                    >
                      <ChevronsUpDown className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                </div>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="end">
                  <Command
                    filter={(value, search) =>
                      value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                    }
                  >
                    <CommandInput placeholder="Search existing names..." />
                    <CommandList>
                      <CommandEmpty>
                        No matching campaign for this client. Type a name above to create a new one.
                      </CommandEmpty>
                      <CommandGroup heading="Existing campaigns for this client">
                        {existingNamesForClient.map((n) => (
                          <CommandItem
                            key={n}
                            value={n}
                            onSelect={() => {
                              handleInputChange('name', n)
                              setNameComboOpen(false)
                            }}
                          >
                            {n}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                New versions of the same campaign must reuse the exact same
                name (e.g. "Cruva - Bulk"). Pick from the list when possible.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="campaign_type">Campaign Type</Label>
              <Select 
                value={formData.campaign_type} 
                onValueChange={(value) => handleInputChange('campaign_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select campaign type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bulk">Bulk Campaigns</SelectItem>
                  <SelectItem value="triggers">Triggers</SelectItem>
                  <SelectItem value="adhoc">Adhoc</SelectItem>
                  <SelectItem value="events">Events</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                value={formData.version}
                onChange={(e) => handleInputChange('version', e.target.value)}
                placeholder="e.g., 1.0, 2.0"
              />
            </div>
          </div>

          {/* Previous Campaign Selection - Always visible */}
          <div className="space-y-2">
            <Label htmlFor="previous_campaign">Link to Previous Campaign (Optional)</Label>
            {formData.client_id ? (
              availableCampaigns.length > 0 ? (
                <Select 
                  value={formData.previous_campaign_id || "none"} 
                  onValueChange={(value) => handleInputChange('previous_campaign_id', value === "none" ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select previous campaign to link" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No previous campaign</SelectItem>
                    {availableCampaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name} {campaign.version && `(v${campaign.version})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="p-3 border rounded-lg bg-muted/20">
                  <p className="text-sm text-muted-foreground">
                    No existing campaigns found for this client. This will be the first campaign.
                  </p>
                </div>
              )
            ) : (
              <div className="p-3 border rounded-lg bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  Please select a client first to see available campaigns to link
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Link this campaign to a previous version for tracking campaign iterations (e.g., v1 → v2)
            </p>
          </div>

          {formData.campaign_type === 'triggers' && (
            <div className="space-y-2">
              <Label>Trigger Type</Label>
              <RadioGroup 
                value={formData.trigger_type} 
                onValueChange={(value) => handleInputChange('trigger_type', value)}
                className="flex flex-col space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="internal" id="internal" />
                  <Label htmlFor="internal" className="text-sm font-normal cursor-pointer">
                    Internal Triggers
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="external" id="external" />
                  <Label htmlFor="external" className="text-sm font-normal cursor-pointer">
                    External Triggers
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          <div className="space-y-2">
            <Label>Channel Selection</Label>
            <div className="grid grid-cols-2 gap-3 p-4 border rounded-lg">
              {['email', 'linkedin', 'cold_call', 'linkedin_ads'].map((channel) => (
                <div key={channel} className="flex items-center space-x-2">
                  <Checkbox
                    id={channel}
                    checked={formData.channels.includes(channel)}
                    onCheckedChange={(checked) => handleChannelChange(channel, checked as boolean)}
                  />
                  <Label htmlFor={channel} className="text-sm font-normal cursor-pointer">
                    {channel === 'cold_call' ? 'Cold Call' : 
                     channel === 'linkedin_ads' ? 'LinkedIn Ads' : 
                     channel === 'linkedin' ? 'LinkedIn' : 'Email'}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter campaign description"
              rows={3}
            />
          </div>

          {/* Old "Target Regions" checkbox removed (Phase I / Q7 2026-04-22).
              Regions are now a variant dimension in the Campaign Structure
              card above — they feed auto-variant generation. The legacy
              campaigns.target_regions TEXT[] column is kept in the DB as
              deprecated metadata; nothing reads it going forward. */}

          <div className="space-y-4">
            <Label className="text-base font-medium">Campaign Links</Label>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="clay_table_link" className="flex items-center gap-2">
                  <Link className="h-4 w-4" />
                  Clay Workbook Link
                </Label>
                <Input
                  id="clay_table_link"
                  type="url"
                  value={formData.clay_table_link}
                  onChange={(e) => handleInputChange('clay_table_link', e.target.value)}
                  placeholder="https://clay.com/..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="clickup_task_link" className="flex items-center gap-2">
                  <CheckSquare className="h-4 w-4" />
                  ClickUp Task Link
                </Label>
                <Input
                  id="clickup_task_link"
                  type="url"
                  value={formData.clickup_task_link}
                  onChange={(e) => handleInputChange('clickup_task_link', e.target.value)}
                  placeholder="https://clickup.com/..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="copywriting_doc_link" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Copywriting Doc Link
                </Label>
                <Input
                  id="copywriting_doc_link"
                  type="url"
                  value={formData.copywriting_doc_link}
                  onChange={(e) => handleInputChange('copywriting_doc_link', e.target.value)}
                  placeholder="https://docs.google.com/..."
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <Label>TAM List</Label>
            
            {/* Source Selection */}
            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="file-source"
                  name="tam-source"
                  checked={tamSource === 'file'}
                  onChange={() => setTamSource('file')}
                  className="w-4 h-4"
                />
                <Label htmlFor="file-source" className="text-sm cursor-pointer">Upload File</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="crm-source"
                  name="tam-source"
                  checked={tamSource === 'crm'}
                  onChange={() => setTamSource('crm')}
                  className="w-4 h-4"
                />
                <Label htmlFor="crm-source" className="text-sm cursor-pointer">Select from Master CRM</Label>
              </div>
            </div>

            {tamSource === 'file' ? (
              <Card>
                <CardContent className="p-4">
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                      isDragActive 
                        ? 'border-primary bg-primary/5' 
                        : 'border-muted-foreground/25 hover:border-primary/50'
                    }`}
                  >
                    <input {...getInputProps()} />
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <div className="text-sm">
                        {tamFile ? (
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            <span className="text-foreground font-medium">{tamFile.name}</span>
                          </div>
                        ) : (
                          <div>
                            <span className="text-foreground">Drop your TAM list here, or </span>
                            <span className="text-primary font-medium">browse</span>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Supports CSV, XLS, XLSX files
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-4">
                  {formData.client_id ? (
                    <div className="space-y-3">
                      <div className="text-sm text-muted-foreground">
                        Select contacts from Master CRM for {clients.find(c => c.id === formData.client_id)?.name}
                      </div>
                      {contacts.length > 0 ? (
                        <div className="max-h-60 overflow-y-auto border rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Available Contacts ({contacts.length})</span>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedContacts(contacts.map(c => c.id))}
                              >
                                Select All
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedContacts([])}
                              >
                                Clear All
                              </Button>
                            </div>
                          </div>
                          {contacts.map((contact) => (
                            <div key={contact.id} className="flex items-center space-x-2 p-2 hover:bg-muted/50 rounded">
                              <Checkbox
                                id={`contact-${contact.id}`}
                                checked={selectedContacts.includes(contact.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedContacts(prev => [...prev, contact.id])
                                  } else {
                                    setSelectedContacts(prev => prev.filter(id => id !== contact.id))
                                  }
                                }}
                              />
                              <Label htmlFor={`contact-${contact.id}`} className="flex-1 cursor-pointer text-sm">
                                <div className="flex justify-between">
                                  <span className="font-medium">{contact.first_name} {contact.last_name}</span>
                                  <span className="text-muted-foreground">{contact.email}</span>
                                </div>
                                {contact.job_title && (
                                  <div className="text-xs text-muted-foreground">{contact.job_title}</div>
                                )}
                              </Label>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-muted-foreground">
                          <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No contacts found for this client in Master CRM</p>
                          <p className="text-xs">Add contacts to Master CRM first</p>
                        </div>
                      )}
                      {selectedContacts.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Please select a client first to view available contacts</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || loading}>
              {isCreating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Campaign...
                </>
              ) : loading ? 'Creating...' : 'Create Campaign'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}